import { create } from "zustand"
import { isProd } from "utils/networking"
import type { User, AuthenticatedUser } from "types/auth"

// IDs of users for which side links should be hidden
const hideSideLinkForProd = [262, 244, 362]
const hideSideLinkForDev = [17]
let hideFor = hideSideLinkForProd

try {
  hideFor = isProd ? hideSideLinkForProd : hideSideLinkForDev
} catch (e) {
  console.error("Hide For error", e)
}

/**
 * Creates an authenticated user state from a user object
 * @param user The user object from the API
 * @param hideForIds Array of user IDs for which to hide side links
 * @return `AuthenticatedUser` object
 */
const createAuthenticatedState = (
  user: User,
  hideForIds: number[]
): AuthenticatedUser => {
  return {
    id: user.id,
    tenant: user?.tenant,
    hasPricePermission: !!user.price_permission,
    approverPermission: user?.approver_permission ?? false,
    approvalRequired: user.approval_required ?? false,
    hideLinks:
      (user?.tenant?.slug === "fuelexpress" &&
        !!user.id &&
        hideForIds.includes(Number(user.id))) ||
      false,
    isExternal:
      window.location.hostname.includes("firstamerican") && !!user?.external_id,
    allowed_customer: user?.customer_id,
    allowed_locations: user?.login_subsets
      ? user.login_subsets
          .split(",")
          .filter((x) => !!x)
          .map((i) => parseInt(i, 10))
      : undefined,
    isAdmin: !!user?.groups?.includes("Admin"),
    isCustomer:
      !!user?.groups?.includes("Customer") && !user?.groups?.includes("Admin"),
    isDriver:
      !!user?.groups?.includes("Driver") && !user?.groups?.includes("Admin"),
    isSalesperson:
      !!user?.groups?.includes("Salesperson") &&
      !user?.groups?.includes("Admin"),
    isCommonCarrier:
      !!user?.is_common_carrier && !user?.groups?.includes("Admin"),
    isCC: !!user?.is_common_carrier && !user?.groups?.includes("Admin"),
  }
}

type UserStore = AuthenticatedUser & {
  set: (user: User) => void
}

const useStore = create<UserStore>((set) => ({
  id: undefined,
  tenant: undefined,
  hasPricePermission: false,
  approverPermission: false,
  approvalRequired: false,
  hideLinks: false,
  isExternal: false,
  allowed_customer: undefined,
  allowed_locations: undefined,
  isAdmin: false,
  isCustomer: false,
  isSalesperson: false,
  isDriver: false,
  isCommonCarrier: false,
  isCC: false,

  set: (user: User) => {
    const authenticatedState = createAuthenticatedState(user, hideFor)
    return set(authenticatedState)
  },
}))

export default useStore
